import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getDeck } from '../services/decks';
import { styled } from '@mui/material/styles';

// Container principal de la carte
const CardContainer = styled(Box)(({ theme, width, height }) => ({
  width: width || 500,
  height: height || 300,
  margin: '0 auto',
  marginBottom: theme.spacing(3)
}));

// La carte elle-même
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isCorrect' && prop !== 'isIncorrect'
})(({ theme, isCorrect, isIncorrect }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(18, 18, 18, 0.8)',
  transition: 'all 0.3s ease-in-out',
  transform: isCorrect ? 'scale(1.05)' : isIncorrect ? 'scale(0.95)' : 'scale(1)',
  border: isCorrect 
    ? '5px solid #4CAF50'
    : isIncorrect 
    ? '5px solid #F44336'
    : '5px solid transparent',
  position: 'relative',
  overflow: 'hidden'
}));

// Conteneur des réponses
const AnswerContainer = styled(Box)(({ theme, width, height }) => ({
  width: width || 500,
  height: height || 200,
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: `repeat(2, ${width ? (width - 16) / 2 : 242}px)`,
  gridTemplateRows: `${height ? height : 200}px`,
  gap: theme.spacing(2)
}));

// Carte de réponse
const AnswerCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isCorrect' && prop !== 'isIncorrect'
})(({ theme, isSelected, isCorrect, isIncorrect, width, height }) => ({
  width: width || 242,
  height: height || 200,
  cursor: 'pointer',
  backgroundColor: 'rgba(18, 18, 18, 0.8)',
  transition: 'all 0.3s ease-in-out',
  transform: 'scale(1)',
  border: isSelected 
    ? isCorrect 
      ? '3px solid #4CAF50'
      : isIncorrect 
        ? '3px solid #F44336'
        : '3px solid #2196F3'
    : '3px solid transparent'
}));

function Quiz() {
  const { id: deckId } = useParams();
  const navigate = useNavigate();
  
  const [deck, setDeck] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [stats, setStats] = useState({ correct: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isDimensionsReady, setIsDimensionsReady] = useState(false);
  const [cardErrors, setCardErrors] = useState({});
  const [seenCards, setSeenCards] = useState(new Set());
  const [isInitialPhase, setIsInitialPhase] = useState(true);
  const [cardsToReview, setCardsToReview] = useState([]);
  const [responsiveDimensions, setResponsiveDimensions] = useState({
    width: 500,
    height: 300,
    answerWidth: 200,
    answerHeight: 200,
    scale: 1
  });
  const [startTime, setStartTime] = useState(null);
  const [, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);

  // Calculer les dimensions responsives
  useEffect(() => {
    const updateDimensions = () => {
      if (!deck) return;

      const viewportHeight = window.innerHeight;
      const viewportWidth = document.documentElement.clientWidth;
      
      const originalWidth = deck.cardWidth || 500;
      const originalHeight = deck.cardHeight || 300;
      const gap = 16; // Espacement entre les cartes de réponse
      
      // Calculer la hauteur totale nécessaire (question + 2 réponses + 2 gaps)
      const totalHeight = originalHeight + 2 * ((originalHeight - gap) / 2) + 2 * gap;
      
      // Calculer le scale basé sur la hauteur disponible
      const responsiveScale = Math.min(1, (viewportHeight - 200) / totalHeight); // 200px pour les marges et le compteur
      
      // Calculer les dimensions finales en appliquant le scale
      const questionWidth = Math.min(viewportWidth - 32, originalWidth);
      const questionHeight = originalHeight * responsiveScale;
      const answerWidth = (questionWidth - gap) / 2;
      const answerHeight = (questionHeight - gap) / 2;

      setResponsiveDimensions({ 
        width: questionWidth,
        height: questionHeight,
        answerWidth: answerWidth,
        answerHeight: answerHeight,
        scale: responsiveScale
      });
      setIsDimensionsReady(true);
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [deck]);

  useEffect(() => {
    loadDeck();
  }, [deckId]);

  const loadDeck = async () => {
    try {
      setIsLoading(true);
      setIsDimensionsReady(false);
      const deckData = await getDeck(deckId);
      
      // Réinitialiser les états avant de mélanger
      setSeenCards(new Set());
      setCardErrors({});
      setIsInitialPhase(true);
      setCardsToReview([]);
      
      // Mélanger toutes les cartes
      deckData.cards = [...deckData.cards].sort(() => Math.random() - 0.5);
      setDeck(deckData);
      
      // Attendre un court instant pour s'assurer que les états sont réinitialisés
      setTimeout(() => {
        generateQuestion(deckData);
      }, 0);
    } catch (error) {
      console.error('Erreur lors du chargement du paquet:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateQuestion = (deckData) => {
    // Si on est en phase initiale
    if (isInitialPhase) {
      // Sélectionner la carte principale (question)
      const availableCards = deckData.cards.filter(card => !seenCards.has(card.id));

      // Si toutes les cartes ont été vues, passer à la phase de révision
      if (availableCards.length === 0) {
        // Calculer la moyenne des erreurs
        const errorScores = deckData.cards.map(card => cardErrors[card.id] || 0);
        const averageErrors = errorScores.reduce((a, b) => a + b, 0) / deckData.cards.length;
        
        // Sélectionner les cartes à réviser (score >= moyenne)
        const cardsAboveAverage = deckData.cards.filter(card => 
          (cardErrors[card.id] || 0) >= averageErrors
        );
        
        // Mélanger ces cartes
        const shuffledCards = [...cardsAboveAverage].sort(() => Math.random() - 0.5);
        
        // Passer en phase de révision
        setIsInitialPhase(false);
        setCardsToReview(shuffledCards);
        setSeenCards(new Set());
        
        // Générer la première question de la phase de révision
        const questionCard = shuffledCards[0];
        setSeenCards(new Set([questionCard.id]));
        
        const isFront = deckData.studyMode === 'front' || 
          (deckData.studyMode === 'random' && Math.random() < 0.5);

        // Sélectionner 3 autres cartes pour les réponses
        const otherCards = shuffledCards
          .filter(card => card.id !== questionCard.id)
          .sort(() => Math.random() - 0.5)
          .slice(0, 3);

        // Compléter avec des cartes du deck complet si nécessaire
        const remainingCards = deckData.cards
          .filter(card => !otherCards.some(c => c.id === card.id) && card.id !== questionCard.id)
          .sort(() => Math.random() - 0.5)
          .slice(0, 3 - otherCards.length);

        // Mélanger les réponses
        const allAnswers = [
          { card: questionCard, isCorrect: true },
          ...otherCards.map(card => ({ card, isCorrect: false })),
          ...remainingCards.map(card => ({ card, isCorrect: false }))
        ].sort(() => Math.random() - 0.5);

        setCurrentQuestion({
          card: questionCard,
          isFront
        });
        setAnswers(allAnswers);
        setSelectedAnswer(null);
        setIsCorrect(null);
        return;
      }

      // Sélectionner la première carte disponible
      const questionCard = availableCards[0];
      
      // Marquer la carte comme vue
      setSeenCards(prev => {
        const newSet = new Set([...prev, questionCard.id]);
        return newSet;
      });
      
      const isFront = deckData.studyMode === 'front' || 
        (deckData.studyMode === 'random' && Math.random() < 0.5);

      // Sélectionner les cartes de réponse
      const otherCards = deckData.cards
        .filter(card => card.id !== questionCard.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);

      // Compléter avec des cartes du deck complet si nécessaire
      const remainingCards = deckData.cards
        .filter(card => !otherCards.some(c => c.id === card.id) && 
                       card.id !== questionCard.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3 - otherCards.length);

      // Mélanger les réponses
      const allAnswers = [
        { card: questionCard, isCorrect: true },
        ...otherCards.map(card => ({ card, isCorrect: false })),
        ...remainingCards.map(card => ({ card, isCorrect: false }))
      ].sort(() => Math.random() - 0.5);

      setCurrentQuestion({
        card: questionCard,
        isFront
      });
      setAnswers(allAnswers);
      setSelectedAnswer(null);
      setIsCorrect(null);
      return;
    }

    // Si on est en phase de révision et qu'on a vu toutes les cartes à réviser
    if (!isInitialPhase && seenCards.size === cardsToReview.length) {
      // Calculer la nouvelle moyenne avec toutes les cartes
      const errorScores = deckData.cards.map(card => cardErrors[card.id] || 0);
      const averageErrors = errorScores.reduce((a, b) => a + b, 0) / deckData.cards.length;
      
      // Sélectionner les nouvelles cartes à réviser
      const newCardsToReview = deckData.cards.filter(card => 
        (cardErrors[card.id] || 0) >= averageErrors
      );
      
      // Si aucune carte n'est au-dessus de la moyenne, réinitialiser
      if (newCardsToReview.length === 0) {
        setCardErrors({});
        setIsInitialPhase(true);
        setCardsToReview([]);
        setSeenCards(new Set());
        return;
      }
      
      // Mélanger ces cartes
      const shuffledCards = [...newCardsToReview].sort(() => Math.random() - 0.5);
      
      setCardsToReview(shuffledCards);
      setSeenCards(new Set());
      
      // Générer la première question de la nouvelle phase de révision
      const questionCard = shuffledCards[0];
      setSeenCards(new Set([questionCard.id]));
      
      const isFront = deckData.studyMode === 'front' || 
        (deckData.studyMode === 'random' && Math.random() < 0.5);

      // Sélectionner 3 autres cartes pour les réponses
      const otherCards = shuffledCards
        .filter(card => card.id !== questionCard.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);

      // Compléter avec des cartes du deck complet si nécessaire
      const remainingCards = deckData.cards
        .filter(card => !otherCards.some(c => c.id === card.id) && card.id !== questionCard.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3 - otherCards.length);

      // Mélanger les réponses
      const allAnswers = [
        { card: questionCard, isCorrect: true },
        ...otherCards.map(card => ({ card, isCorrect: false })),
        ...remainingCards.map(card => ({ card, isCorrect: false }))
      ].sort(() => Math.random() - 0.5);

      setCurrentQuestion({
        card: questionCard,
        isFront
      });
      setAnswers(allAnswers);
      setSelectedAnswer(null);
      setIsCorrect(null);
      return;
    }

    // Sélectionner la carte principale (question) pour la phase de révision
    const availableCards = cardsToReview.filter(card => !seenCards.has(card.id));

    if (availableCards.length === 0) {
      return;
    }

    // Mélanger les cartes disponibles
    const shuffledAvailableCards = [...availableCards].sort(() => Math.random() - 0.5);

    // Sélectionner la carte principale (question)
    const questionCard = shuffledAvailableCards[0];
    
    // Marquer la carte comme vue
    setSeenCards(prev => new Set([...prev, questionCard.id]));
    
    const isFront = deckData.studyMode === 'front' || 
      (deckData.studyMode === 'random' && Math.random() < 0.5);

    // Sélectionner les cartes de réponse
    const otherCards = cardsToReview
      .filter(card => card.id !== questionCard.id)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);

    // Compléter avec des cartes du deck complet si nécessaire
    const remainingCards = deckData.cards
      .filter(card => !otherCards.some(c => c.id === card.id) && 
                     card.id !== questionCard.id)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3 - otherCards.length);

    // Mélanger les réponses
    const allAnswers = [
      { card: questionCard, isCorrect: true },
      ...otherCards.map(card => ({ card, isCorrect: false })),
      ...remainingCards.map(card => ({ card, isCorrect: false }))
    ].sort(() => Math.random() - 0.5);

    setCurrentQuestion({
      card: questionCard,
      isFront
    });
    setAnswers(allAnswers);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  // Ajout du timer
  useEffect(() => {
    if (currentQuestion && !selectedAnswer) {
      setStartTime(Date.now());
      const interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
      setTimerInterval(interval);
    }
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [currentQuestion, selectedAnswer]);

  const handleAnswerSelect = (answer) => {
    if (selectedAnswer || !currentQuestion) return;
    
    // Arrêter le timer
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    
    setSelectedAnswer(answer);
    const isCorrect = answer.card.id === currentQuestion.card.id;
    setIsCorrect(isCorrect);
    
    // Mettre à jour les statistiques
    setStats(prev => ({ 
      ...prev, 
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1 
    }));
    
    // Calculer le temps de réponse en secondes
    const responseTime = (Date.now() - startTime) / 1000;
    
    // Mettre à jour le score d'erreur en fonction du temps de réponse
    if (isCorrect) {
      // Pour les bonnes réponses, le score diminue en fonction du temps
      // Plus la réponse est rapide, plus le score diminue
      let scoreAdjustment;
      if (responseTime < 2) {
        // Réponse très rapide (< 2s) : -2 points
        scoreAdjustment = -2;
      } else if (responseTime < 5) {
        // Réponse rapide (2-5s) : -1.5 points
        scoreAdjustment = -1.5;
      } else if (responseTime < 10) {
        // Réponse normale (5-10s) : -1 point
        scoreAdjustment = -1;
      } else {
        // Réponse lente (>10s) : -0.5 point
        scoreAdjustment = -0.5;
      }
      
      setCardErrors(prev => ({
        ...prev,
        [currentQuestion.card.id]: (prev[currentQuestion.card.id] || 0) + scoreAdjustment
      }));
    } else {
      // Réponse incorrecte : +1 point si rapide, +2 points si lente
      const scoreAdjustment = responseTime > 10 ? 2 : 1;
      
      setCardErrors(prev => ({
        ...prev,
        [currentQuestion.card.id]: (prev[currentQuestion.card.id] || 0) + scoreAdjustment
      }));
    }

    // Attendre que les états soient mis à jour avant de générer la prochaine question
    setTimeout(() => {
      if (deck) {
        generateQuestion(deck);
      }
    }, 1500);
  };

  // Fonction pour calculer la taille de police adaptative
  const calculateFontSize = (isAnswer = false, scale = 1) => {
    if (!deck) return 24;
    
    // Utiliser la taille du deck pour la question
    const baseSize = isAnswer ? 
      (deck.backFontSize || 24) / 2 : // Pour les réponses, diviser par 2
      (deck.frontFontSize || 24);     // Pour la question, utiliser la taille normale
    
    // Appliquer l'échelle responsive
    return Math.round(baseSize * scale);
  };

  if (isLoading || !isDimensionsReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/dashboard')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          Score : {stats.correct} / {stats.total}
        </Typography>
      </Box>

      {currentQuestion && (
        <>
          <CardContainer width={responsiveDimensions.width} height={responsiveDimensions.height}>
            <StyledCard isCorrect={isCorrect === true} isIncorrect={isCorrect === false}>
              <CardContent sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                position: 'relative'
              }}>
                {currentQuestion.card.frontImageUrl && currentQuestion.isFront && (
                  <Box
                    component="img"
                    src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${currentQuestion.card.frontImageUrl}`}
                    alt=""
                    sx={{
                      width: '100%',
                      maxHeight: '80%',
                      objectFit: 'contain',
                      mb: 2
                    }}
                  />
                )}
                {currentQuestion.card.backImageUrl && !currentQuestion.isFront && (
                  <Box
                    component="img"
                    src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${currentQuestion.card.backImageUrl}`}
                    alt=""
                    sx={{
                      width: '100%',
                      maxHeight: '80%',
                      objectFit: 'contain',
                      mb: 2
                    }}
                  />
                )}
                <Typography 
                  variant="h5"
                  sx={{ 
                    color: currentQuestion.isFront ? currentQuestion.card.frontColor : currentQuestion.card.backColor,
                    fontSize: `${calculateFontSize(false, responsiveDimensions.scale)}px`,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {currentQuestion.isFront ? currentQuestion.card.front : currentQuestion.card.back}
                </Typography>
              </CardContent>
            </StyledCard>
          </CardContainer>

          <AnswerContainer 
            width={responsiveDimensions.width} 
            height={responsiveDimensions.answerHeight}
          >
            {answers.map((answer, index) => (
              <AnswerCard
                key={index}
                width={responsiveDimensions.answerWidth}
                height={responsiveDimensions.answerHeight}
                isSelected={selectedAnswer === answer}
                isCorrect={selectedAnswer === answer && answer.isCorrect}
                isIncorrect={selectedAnswer === answer && !answer.isCorrect}
                onClick={() => handleAnswerSelect(answer)}
              >
                <CardContent sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  position: 'relative'
                }}>
                  {currentQuestion.isFront ? (
                    answer.card.backImageUrl && (
                      <Box
                        component="img"
                        src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${answer.card.backImageUrl}`}
                        alt=""
                        sx={{
                          width: '100%',
                          maxHeight: '80%',
                          objectFit: 'contain',
                          mb: 1
                        }}
                      />
                    )
                  ) : (
                    answer.card.frontImageUrl && (
                      <Box
                        component="img"
                        src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${answer.card.frontImageUrl}`}
                        alt=""
                        sx={{
                          width: '100%',
                          maxHeight: '80%',
                          objectFit: 'contain',
                          mb: 1
                        }}
                      />
                    )
                  )}
                  <Typography 
                    variant="h6"
                    sx={{ 
                      color: currentQuestion.isFront ? answer.card.backColor : answer.card.frontColor,
                      fontSize: `${calculateFontSize(true, responsiveDimensions.scale)}px`,
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {currentQuestion.isFront ? answer.card.back : answer.card.front}
                  </Typography>
                </CardContent>
              </AnswerCard>
            ))}
          </AnswerContainer>
        </>
      )}
    </Container>
  );
}

export default Quiz; 